import { trackDesignSystemAction } from 'analytics';
import { useCallback, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { IconType } from 'components/Icon';
import Button, { ButtonState, ButtonType } from 'components/Widget/Button/Button';
import {
	Navbar,
	NavbarLeft,
	NavbarCenter,
	NavbarRight,
	NavbarButton,
	NavbarTitle,
} from 'components/Widget/Navbar';
import { DEFAULT_DS_TITLE } from 'dragonmouse-shared/constants';
import { MenuItemAction } from 'menu/MenuItem';
import * as S from './styles';
import { RootReducer } from 'redux/reducers';
import { selectActiveDesignSystem, selectActiveDesignSystemVersion } from 'redux/db/reducers';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { showContextMenu } from 'redux/mode/actions';
import {
	displayConfirmModal,
	displayDsReadOnlyModal,
	displayPublishDsModal,
} from 'redux/mode/util';
import { designSystemEditingBlocked } from 'redux/db/util';
import { interceptIfNoCredits } from 'economy';
import { toast } from 'react-toastify';
import { Loader } from 'components/Widget/Loader';
import ShareDesignSystemModal from 'components/Modal/Share/ShareDesignSystemModal';
import JSZip from 'jszip';
import { downloadBlob } from 'utils';
import { VITE_FORGE_URL } from '@constants';
import forge from 'apis/forge';
import { useNavigate } from 'react-router';
import * as routes from 'routes';
import PreviewBgPicker from './PreviewBgPicker';
import Search from './Search';
import VersionDropdown from './VersionDropdown';

// Width thresholds based on window.innerWidth
const WIDTH_SEARCH_ICON = 1000;
const WIDTH_SHARE_ICON = 900;
const WIDTH_SEARCH_HIDDEN = 700;
const WIDTH_DOWNLOAD_HIDDEN = 600;

const TopBar = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const {
		activeDesignSystem,
		activeDesignSystemVersion,
		designSystemContent,
		isPreparing,
		historicalDsVersionNumber,
		isReadOnly,
	} = useSelector(
		(r: RootReducer) => ({
			activeDesignSystem: selectActiveDesignSystem(r.db, r.mode.activeDesignSystemId),
			activeDesignSystemVersion: selectActiveDesignSystemVersion(r.db),
			designSystemContent: r.mode.designSystemContent,
			isPreparing: r.db.dsPreparing,
			historicalDsVersionNumber: r.mode.historicalDsVersionNumber,
			isReadOnly:
				!!r.mode.activeDesignSystemId &&
				!r.db.designSystems.some((ds) => ds.id === r.mode.activeDesignSystemId),
		}),
		shallowEqual
	);
	const [showShareModal, setShowShareModal] = useState(false);
	const [isDownloading, setIsDownloading] = useState(false);
	const [windowWidth, setWindowWidth] = useState(
		typeof window !== 'undefined' ? window.innerWidth : 1200
	);

	useEffect(() => {
		const onResize = () => {
			setWindowWidth(window.innerWidth);
		};
		window.addEventListener('resize', onResize);
		return () => window.removeEventListener('resize', onResize);
	}, []);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const onDuplicate = useCallback(() => {
		if (!activeDesignSystem?.id) return;
		const sourceVersion = activeDesignSystemVersion?.versionNumber;
		if (sourceVersion == null) return;
		displayConfirmModal({
			title: 'Duplicate design system',
			description: 'This will create your own editable copy of this design system.',
			submitLabel: 'Duplicate',
			onSubmit: () => {
				const promise = (async () => {
					const res = await forge.duplicateDesignSystem({
						sourceDesignSystemId: activeDesignSystem.id,
						sourceVersionNumber: sourceVersion,
					});
					trackDesignSystemAction('duplicate', { source: 'readonly_topbar' });
					navigate(routes.designSystem(res.designSystem.id, res.designSystem.title));
				})();
				toast.promise(promise, {
					pending: 'Duplicating design system...',
					success: 'Design system duplicated',
					error: 'Failed to duplicate design system',
				});
			},
		});
	}, [activeDesignSystem?.id, activeDesignSystemVersion?.versionNumber, navigate]);

	const onDownloadDs = async () => {
		trackDesignSystemAction('download');
		// designSystemContent.files comes from the active version (draft if exists, otherwise latest published)
		// This matches what the user sees on screen, so the download will match the current view
		const files = designSystemContent?.files;
		if (!files || files.length === 0) {
			toast.error('No design system content available to download');
			return;
		}

		setIsDownloading(true);

		try {
			const zip = new JSZip();

			// Add text files (non-assets) - content is stored directly
			for (const file of files) {
				if (file.path.startsWith('assets/')) continue; // Handle separately
				zip.file(file.path, file.content);
			}

			// Fetch and add asset files
			// Assets have their content field set to a CAS storage path, not the actual content.
			// We use ?proxy=true to get the content directly (avoids CORS issues with signed URL redirects).
			const assetFiles = files.filter((f) => f.path.startsWith('assets/'));
			if (assetFiles.length > 0) {
				const failedAssets: string[] = [];

				const assetPromises = assetFiles.map(async (file) => {
					try {
						// file.content is the CAS storage path for assets
						const storagePath = encodeURIComponent(file.content);
						const response = await fetch(`${VITE_FORGE_URL}/attachment/${storagePath}?proxy=true`, {
							credentials: 'include',
						});

						if (!response.ok) {
							failedAssets.push(file.path);
							return null;
						}

						const blob = await response.blob();
						return { path: file.path, blob };
					} catch {
						failedAssets.push(file.path);
						return null;
					}
				});

				const assetResults = await Promise.all(assetPromises);

				for (const result of assetResults) {
					if (result) {
						zip.file(result.path, result.blob);
					}
				}

				if (failedAssets.length > 0) {
					Sentry.captureMessage(
						`DS ZIP download missing assets: ${failedAssets.join(', ')}`,
						'warning'
					);
				}
			}

			const zipBlob = await zip.generateAsync({ type: 'blob' });
			const trimmedTitle = activeDesignSystem?.title?.trim();
			const filename = trimmedTitle
				? `${trimmedTitle.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-ds.zip`
				: 'design-system.zip';

			// Reset loading state before showing file dialog (dialog is the confirmation)
			setIsDownloading(false);

			downloadBlob(zipBlob, filename);
		} catch (error) {
			toast.error('Failed to create download file');
			setIsDownloading(false);
		}
	};

	const isDraft = activeDesignSystemVersion?.versionNumber === 0 && !isPreparing;
	const isViewingHistorical = historicalDsVersionNumber !== null;

	const searchMode =
		windowWidth < WIDTH_SEARCH_HIDDEN
			? 'hidden'
			: windowWidth < WIDTH_SEARCH_ICON
			? 'iconOnly'
			: 'full';
	const showShareText = windowWidth >= WIDTH_SHARE_ICON;
	const showDownload = windowWidth >= WIDTH_DOWNLOAD_HIDDEN;

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	const onPublish = () => {
		if (!activeDesignSystem?.id) return;
		const blocked = designSystemEditingBlocked();
		if (blocked) {
			displayDsReadOnlyModal(blocked);
			return;
		}
		if (interceptIfNoCredits({ kind: 'chatEdit', designSystemId: activeDesignSystem.id })) {
			return;
		}
		displayPublishDsModal(activeDesignSystem.id, activeDesignSystem.title);
	};

	return (
		<>
			<Navbar layout="left-aligned">
				<NavbarLeft>
					<S.TitleWrapper
						onContextMenu={(e) => {
							if (activeDesignSystem) {
								dispatch(
									showContextMenu({
										point: { x: e.clientX + 5, y: e.clientY },
										menuItems: [MenuItemAction.createRenameDesignSystem(activeDesignSystem)],
									})
								);
							}
						}}
					>
						<NavbarTitle>{activeDesignSystem?.title || DEFAULT_DS_TITLE}</NavbarTitle>
					</S.TitleWrapper>
					<VersionDropdown />
				</NavbarLeft>

				{isReadOnly && (
					<NavbarCenter>
						<S.ReadOnlyBanner>
							Viewing · <S.ReadOnlyLink onClick={onDuplicate}>Duplicate to edit</S.ReadOnlyLink>
						</S.ReadOnlyBanner>
					</NavbarCenter>
				)}

				{!isPreparing && (
					<NavbarRight>
					<Search compactMode={searchMode} />
						<PreviewBgPicker />
						{showDownload && (
							<S.Download>
								<NavbarButton
									icon={isDownloading ? undefined : IconType.LINE_ARROW_DOWN}
									onClick={onDownloadDs}
									tooltip={
										isDownloading
											? 'Preparing download... Fetching files and assets'
											: 'Download DS'
									}
									tooltipId="download-kit-tooltip"
									disabled={isDownloading}
								>
									{isDownloading && <Loader width={16} height={16} />}
								</NavbarButton>
							</S.Download>
						)}
						{!isReadOnly && (
							<S.PublishButton>
								<Button
									onSelected={onPublish}
									type={ButtonType.PRIMARY}
									state={
										isDraft && !isViewingHistorical ? ButtonState.REGULAR : ButtonState.DISABLED
									}
								>
									Publish
								</Button>
							</S.PublishButton>
						)}
						<S.Share>
							{showShareText ? (
								<Button
									onSelected={() => {
										trackDesignSystemAction('open_share_modal');
										setShowShareModal(true);
									}}
									type={ButtonType.NOTICEABLE}
								>
									Share
								</Button>
							) : (
								<NavbarButton
									icon={IconType.SHARE}
									onClick={() => {
										trackDesignSystemAction('open_share_modal');
										setShowShareModal(true);
									}}
									tooltip="Share"
									tooltipId="share-design-system-tooltip"
								/>
							)}
						</S.Share>
					</NavbarRight>
				)}
			</Navbar>
			{showShareModal && activeDesignSystem && (
				<ShareDesignSystemModal
					designSystem={activeDesignSystem}
					onClose={() => setShowShareModal(false)}
				/>
			)}
		</>
	);
};

export default TopBar;
